/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BaseBlock } from "../../../block/BaseBlock";
import { TextBlock } from "../../../block/block-types";
import { BlockFactory } from "../../../block/BlockFactory";
import { PositionUtils } from "../../../utilities/PositionUtils";
import { StyleConstants } from "../../../utilities/StyleConstants";
import { SelectCommand } from "src/classes/plate/Command";
import { Key, KeyEventType, KeyHandler } from "src/classes/plate/Keyboard";
import { Plate } from "src/classes/plate/Plate";
import { BaseSession } from "src/classes/plate/Session";
import { DragCanvasSessionSnapshot, DragMode, SessionType } from "src/classes/plate/Session/session.types";
import { MathUtils } from "src/classes/utilities/MathUtils";
export class DragCanvasSession extends BaseSession<DragCanvasSessionSnapshot> {
  plate: Plate;

  private onAKeydown: KeyHandler;

  private onAKeyup: KeyHandler;

  private constructor(snapshot: DragCanvasSessionSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
    this.onAKeydown = new KeyHandler(Key.A, this.handleAKeydown.bind(this), KeyEventType.Down);
    this.onAKeyup = new KeyHandler(Key.A, this.handleAKeyup.bind(this), KeyEventType.Up);
    this.plate.keyboardManager.addKeyHandler(this.onAKeydown);
    this.plate.keyboardManager.addKeyHandler(this.onAKeyup);
    this.onDragCanvas = this.onDragCanvas.bind(this);
  }

  /* -------------------------------------------------- */
  /*                BaseSession Methods                 */
  /* -------------------------------------------------- */
  public start(): void {
    document.addEventListener("mousemove", this.onDragCanvas);
  }

  public cancel(): void {
    document.removeEventListener("mousemove", this.onDragCanvas);
  }

  public end(): void {
    document.removeEventListener("mousemove", this.onDragCanvas);
    this.plate.keyboardManager.removeKeyHandler(this.onAKeydown);
    this.plate.keyboardManager.removeKeyHandler(this.onAKeyup);
    const isDoingMultiselect = this.plate.keyboardManager.isKeyPressed(Key.A);

    if (!isDoingMultiselect) {
      const { anchor, focus } = this.snapshot;
      const left = Math.min(anchor.x, focus.x);
      const top = Math.min(anchor.y, focus.y);
      const height = Math.abs(anchor.y - focus.y);
      const width = Math.abs(anchor.x - focus.x);

      if (height >= StyleConstants.minBlockHeight && width >= StyleConstants.minBlockWidth) {
        const baseSnapshot = BaseBlock.newBaseBlockSnapshot({
          top,
          left,
          width,
          height,
          zIndex: this.plate.highestZIndex.value + 1,
          plateId: this.plate.snapshot.id,
        });
        const textSnapshot = TextBlock.newSnapshot(baseSnapshot);
        const block = BlockFactory.create(textSnapshot, this.plate.socket);
        this.plate.createBlocks([block]);
        this.plate.select([block.snapshot.id]);
      }
    } else {
      const cmd = new SelectCommand([], this.plate.snapshot.selection, this.plate);
      if (this.plate.snapshot.selection.length) {
        this.plate.addToHistory(cmd); // if you selected as part of the drag, add to history
      }
    }
  }

  /* -------------------------------------------------- */
  /*                Main Event Handler                  */
  /* -------------------------------------------------- */

  private onDragCanvas(): void {
    this.updateFocus();
    if (this.plate.keyboardManager.isKeyPressed(Key.A)) {
      this.setState({ mode: DragMode.Multiselect });
      this.selectOverlappingBlocks();
    }
  }

  /* -------------------------------------------------- */
  /*                  State Managment                   */
  /* -------------------------------------------------- */
  private updateFocus() {
    const isPreservingAR = this.plate.keyboardManager.isKeyPressed(Key.Shift);
    const aspectRatio = isPreservingAR ? 1 : null;

    const { dx, dy } = MathUtils.getDistanceBetweenPoints(
      this.plate.mouseTracker.snapshot.position,
      this.snapshot.anchor,
      false,
      aspectRatio
    );
    const focus = { x: this.snapshot.anchor.x + dx, y: this.snapshot.anchor.y + dy };
    this.setState({ focus });
  }

  private selectOverlappingBlocks(): void {
    const selectionRectangle = MathUtils.getRectangleFromPoints(this.snapshot.anchor, this.snapshot.focus);

    const overlappingBlockIds = this.plate.blocks
      .filter((block) => {
        const { top, left, width, height } = block.snapshot;
        const blockRect = { top, left, right: left + width, bottom: top + height };
        return PositionUtils.rectsOverlap(selectionRectangle, blockRect);
      })
      .map((block) => block.snapshot.id);

    if (this.plate.snapshot.selection.length !== overlappingBlockIds.length) {
      this.plate.select(overlappingBlockIds, { addToStack: false });
    }
  }

  /* -------------------------------------------------- */
  /*                    Key Handlers                    */
  /* -------------------------------------------------- */
  public handleAKeydown(): void {
    this.setState({ mode: DragMode.Multiselect });
    this.selectOverlappingBlocks();
  }

  public handleAKeyup(): void {
    this.setState({ mode: DragMode.Create });
    this.plate.deselectAll({ addToStack: false });
  }

  /* -------------------------------------------------- */
  /*                    Static Methods                  */
  /* -------------------------------------------------- */

  public static create(plate: Plate): DragCanvasSession {
    const snapshot = DragCanvasSession.getInitialSnapshot(plate);
    return new DragCanvasSession(snapshot, plate);
  }

  private static getInitialSnapshot(plate: Plate): DragCanvasSessionSnapshot {
    return {
      type: SessionType.DragCanvas,
      anchor: { x: plate.mouseTracker.snapshot.position.x, y: plate.mouseTracker.snapshot.position.y },
      focus: { x: plate.mouseTracker.snapshot.position.x, y: plate.mouseTracker.snapshot.position.y },
      mode: DragMode.Create,
      dimensions: { width: 0, height: 0 },
    };
  }
}
